<template>
  <div class="relative" ref="rootRef">
    <button
      type="button"
      :class="buttonClass"
      :title="$t('notifications.common.title')"
      :aria-label="$t('notifications.common.title')"
      data-test="notification-bell"
      @click="toggle"
    >
      <!-- Bell glyph (inline SVG so we don't need an icon dependency) -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        class="w-5 h-5 fill-current"
        aria-hidden="true"
      >
        <path
          d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C7.64 5.36 6 7.93 6 11v5l-2 2v1h16v-1l-2-2z"
        />
      </svg>
      <!-- Unread badge -->
      <span
        v-if="unreadCount > 0"
        class="pointer-events-none absolute right-1 top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full
          bg-theme-danger px-1 text-[10px] font-bold text-theme-text-inverse ring-2 ring-theme-surface"
        data-test="notification-unread-badge"
      >
        {{ unreadDisplay }}
      </span>
    </button>

    <NotificationDropdown
      :open="open"
      :items="dropdownItems"
      :unread-count="unreadCount"
      :loading="loading"
      @close="close"
      @mark-read="onMarkRead"
      @mark-all-read="onMarkAllRead"
      @delete="onDelete"
      @cta-click="close"
    />
  </div>
</template>

<script>
  import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from 'vue';
  import { useNotificationsStore } from '@/features/notifications/stores/useNotificationsStore';
  import { afterCriticalContent } from '@/utils/deferNonCritical';
  import { storeToRefs } from 'pinia';

  import NotificationDropdown from './NotificationDropdown.vue';

  export default defineComponent({
    name: 'NotificationBell',
    components: { NotificationDropdown },
    props: {
      buttonClass: {
        type: [String, Array, Object],
        default:
          'relative rounded-theme-md p-2 text-theme-text-secondary transition-colors hover:bg-theme-surface-raised hover:text-theme-text focus:outline-none focus:shadow-theme-focus',
      },
    },
    setup() {
      const notificationsStore = useNotificationsStore();
      const { unreadCount, recentLoading, recentIds, recentById } = storeToRefs(notificationsStore);
      const open = ref(false);
      const rootRef = ref(null);

      const loading = computed(() => Boolean(recentLoading.value));
      // The dropdown reads the recent-ledger slice (Redis-backed, instant) — kept
      // separate from the paginated full-page list so page filters never leak in.
      const dropdownItems = computed(() =>
        (recentIds.value || [])
          .slice(0, 10)
          .map((id) => recentById.value?.[id])
          .filter(Boolean)
      );
      const unreadDisplay = computed(() =>
        unreadCount.value > 99 ? '99+' : String(unreadCount.value)
      );

      function onDocumentClick(event) {
        if (!rootRef.value) return;
        if (!rootRef.value.contains(event.target)) open.value = false;
      }
      function onEscape(event) {
        if (event.key === 'Escape') open.value = false;
      }

      function toggle() {
        // The list is preloaded on mount via `notifications/start` and kept
        // fresh by SSE + polling, so opening the dropdown does NOT trigger a
        // refresh — items are already present and rendering is instant.
        open.value = !open.value;
      }
      function close() {
        open.value = false;
      }

      async function onMarkRead(id) {
        try {
          await notificationsStore.markRead(id);
        } catch {
          // toast handled by the store action if needed
        }
      }
      async function onMarkAllRead() {
        try {
          await notificationsStore.markAllRead();
        } catch {
          // ignore
        }
      }
      async function onDelete(id) {
        try {
          await notificationsStore.deleteNotification(id);
        } catch {
          // ignore
        }
      }

      let cancelStart = () => {};
      onMounted(() => {
        document.addEventListener('mousedown', onDocumentClick);
        document.addEventListener('keydown', onEscape);
        // The badge is not needed for first paint: start the feed (recent
        // ledger + realtime) once the page's main content has settled.
        cancelStart = afterCriticalContent(() => notificationsStore.start());
      });
      onBeforeUnmount(() => {
        cancelStart();
        document.removeEventListener('mousedown', onDocumentClick);
        document.removeEventListener('keydown', onEscape);
      });

      return {
        open,
        rootRef,
        unreadCount,
        unreadDisplay,
        loading,
        dropdownItems,
        toggle,
        close,
        onMarkRead,
        onMarkAllRead,
        onDelete,
      };
    },
  });
</script>
